import { api } from '@hit-app/convex';
import { useAction } from 'convex/react';
import type { FunctionReturnType } from 'convex/server';
import { useEffect, useState } from 'react';
import { useWorkOSAuth } from './auth';
import { reportPostHogError } from './posthog';

type UserOrganization = FunctionReturnType<typeof api.workos.getUserOrganizations>['memberships'][number];

export interface UserOrganizations {
  memberships: UserOrganization[];
  error: string | null;
  isLoading: boolean;
}

type LoadedOrganizations = { userId: string; memberships: UserOrganization[]; error: string | null };

/**
 * WorkOS organization memberships of the signed-in user (via a Convex action, so not reactive).
 * Loads once per mount and user; the app shell stays mounted across navigation.
 */
export function useUserOrganizations(): UserOrganizations {
  const { user, organizationId } = useWorkOSAuth();
  const userId = user?.id;
  const getUserOrganizations = useAction(api.workos.getUserOrganizations);
  const [loaded, setLoaded] = useState<LoadedOrganizations | null>(null);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    getUserOrganizations().then(
      (result) => {
        if (active) setLoaded({ userId, memberships: result.memberships, error: result.error });
      },
      (error: unknown) => {
        if (!active) return;
        console.error('Error loading organizations:', error);
        reportPostHogError(error, {
          area: 'organization',
          action: 'load_memberships',
          route: '/organization-switcher',
          organizationId,
        });
        setLoaded((previous) => ({
          userId,
          memberships: previous?.userId === userId ? previous.memberships : [],
          error: 'Fehler beim Laden der Vereine',
        }));
      },
    );
    return () => {
      active = false;
    };
  }, [getUserOrganizations, organizationId, userId]);

  const current = loaded && loaded.userId === userId ? loaded : null;
  return {
    memberships: current?.memberships ?? [],
    error: current?.error ?? null,
    isLoading: Boolean(userId) && current === null,
  };
}
